import type { QuotePassenger, QuoteRecord } from '../types';
import { normalizeDateOfBirth } from './passengerBirthDate';
export type BookingPassengerGender = 'male' | 'female' | '';

function parsePassengerName(full: string) {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', middle: '', last: '' };
  if (parts.length === 1) return { first: parts[0]!, middle: '', last: '' };
  if (parts.length === 2) return { first: parts[0]!, middle: '', last: parts[1]! };
  return { first: parts[0]!, middle: parts.slice(1, -1).join(' '), last: parts[parts.length - 1]! };
}

export type BookingPassengerFormRow = {
  id: string;
  gender: BookingPassengerGender;
  firstName: string;
  middleName: string;
  lastName: string;
  /** ISO YYYY-MM-DD */
  dateOfBirth: string;
};

export type BookingPassengerFieldErrors = {
  gender: boolean;
  lastName: boolean;
  firstName: boolean;
  dateOfBirth: boolean;
};

const DOB_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** Confirmation dialog: 01 Jan, 1980 */
export function formatDobConfirmDisplay(iso?: string): string {
  const norm = normalizeDateOfBirth(iso);
  if (!norm) return '———';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(norm);
  if (!m) return '———';
  const monthIdx = Number(m[2]) - 1;
  const mon = DOB_MONTHS[monthIdx];
  if (!mon) return '———';
  return `${m[3]} ${mon}, ${m[1]}`;
}

export function formatGenderConfirmLabel(gender: BookingPassengerGender): string {
  if (gender === 'male') return 'Male';
  if (gender === 'female') return 'Female';
  return '———';
}

/** Display like reference: 01-Jan-1980 */
export function formatDobDisplay(iso?: string): string {
  const norm = normalizeDateOfBirth(iso);
  if (!norm) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(norm);
  if (!m) return '';
  const monthIdx = Number(m[2]) - 1;
  const mon = DOB_MONTHS[monthIdx];
  if (!mon) return '';
  return `${m[3]}-${mon}-${m[1]}`;
}

export function parseDobDisplay(text: string): string | undefined {
  const t = text.trim();
  if (!t) return undefined;
  const fromIso = normalizeDateOfBirth(t);
  if (fromIso) return fromIso;
  const m = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(t);
  if (!m) return undefined;
  const day = Number(m[1]);
  const monStr = m[2]!.charAt(0).toUpperCase() + m[2]!.slice(1).toLowerCase();
  const year = Number(m[3]);
  const monthIdx = DOB_MONTHS.findIndex((x) => x.toLowerCase() === monStr.toLowerCase());
  if (monthIdx < 0 || day < 1 || day > 31 || year < 1900 || year > 2100) return undefined;
  const iso = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return normalizeDateOfBirth(iso);
}

export function fieldErrorsForRow(row: BookingPassengerFormRow): BookingPassengerFieldErrors {
  return {
    gender: row.gender !== 'male' && row.gender !== 'female',
    lastName: !row.lastName.trim(),
    firstName: !row.firstName.trim(),
    dateOfBirth: !normalizeDateOfBirth(row.dateOfBirth),
  };
}

export function newBookingPassengerRow(seed?: Partial<BookingPassengerFormRow>): BookingPassengerFormRow {
  return {
    id: seed?.id ?? `pax_${Math.random().toString(36).slice(2, 10)}`,
    gender: seed?.gender ?? '',
    firstName: seed?.firstName ?? '',
    middleName: seed?.middleName ?? '',
    lastName: seed?.lastName ?? '',
    dateOfBirth: seed?.dateOfBirth ?? '',
  };
}

function ageFromDateOfBirth(iso: string, referenceTravelDate?: string): number | undefined {
  const dob = normalizeDateOfBirth(iso);
  if (!dob) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  if (!m) return undefined;
  const ref =
    referenceTravelDate && /^\d{4}-\d{2}-\d{2}$/.test(referenceTravelDate.trim())
      ? new Date(`${referenceTravelDate.trim()}T12:00:00Z`)
      : new Date();
  const birth = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  let age = ref.getUTCFullYear() - birth.getUTCFullYear();
  const monthDiff = ref.getUTCMonth() - birth.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getUTCDate() < birth.getUTCDate())) age -= 1;
  return age >= 1 && age <= 120 ? age : undefined;
}

export function buildPassengerFullName(row: BookingPassengerFormRow): string {
  return [row.lastName, row.firstName, row.middleName]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' ')
    .toUpperCase();
}

export function bookingFormFromQuotePassenger(p: QuotePassenger): BookingPassengerFormRow {
  const parsed = parsePassengerName(p.name);
  const first = p.firstName?.trim() || parsed.first;
  const middle = p.middleName?.trim() || parsed.middle;
  const last = p.lastName?.trim() || parsed.last;
  const gender =
    p.gender === 'male' || p.gender === 'female' ? p.gender : ('' as BookingPassengerGender);
  return newBookingPassengerRow({
    firstName: first,
    middleName: middle,
    lastName: last,
    dateOfBirth: normalizeDateOfBirth(p.dateOfBirth) ?? '',
    gender,
  });
}

/** Start with one empty row, or N rows from quote adults / saved passengers. */
export function initialBookingPassengerForms(quote: QuoteRecord): BookingPassengerFormRow[] {
  const saved = (quote.passengers ?? []).filter((p) => p.name.trim());
  if (saved.length > 0) {
    return saved.map((p) => bookingFormFromQuotePassenger(p));
  }
  const count = Math.max(1, Math.min(9, quote.adults ?? 1));
  return Array.from({ length: count }, () => newBookingPassengerRow());
}

export function validateBookingPassengerForms(rows: BookingPassengerFormRow[]): string | null {
  if (rows.length === 0) return 'Add at least one passenger.';
  if (rows.length > 9) return 'Maximum 9 passengers.';
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i]!;
    const label = rows.length > 1 ? `passenger ${i + 1}` : 'passenger';
    if (row.gender !== 'male' && row.gender !== 'female') {
      return `Select gender for ${label}.`;
    }
    if (!row.lastName.trim()) return `Enter last name for ${label}.`;
    if (!row.firstName.trim()) return `Enter first name for ${label}.`;
    if (!normalizeDateOfBirth(row.dateOfBirth)) return `Enter date of birth for ${label}.`;
  }
  return null;
}

export function bookingFormsToQuotePassengers(
  rows: BookingPassengerFormRow[],
  referenceTravelDate?: string,
  existingTicketNumbers?: string[],
): QuotePassenger[] {
  return rows.map((row, i) => {
    const dateOfBirth = normalizeDateOfBirth(row.dateOfBirth)!;
    const age = ageFromDateOfBirth(dateOfBirth, referenceTravelDate)!;
    return {
      name: buildPassengerFullName(row),
      age,
      dateOfBirth,
      gender: row.gender === 'male' || row.gender === 'female' ? row.gender : undefined,
      firstName: row.firstName.trim() || undefined,
      middleName: row.middleName.trim() || undefined,
      lastName: row.lastName.trim() || undefined,
      ...(existingTicketNumbers?.[i] ? { eTicketNumber: existingTicketNumbers[i] } : {}),
    };
  });
}
